/**
 * TenantsTab Component
 * Tenant management within Super Admin page
 */

import React, { useState } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  Building2,
  Users,
  Target,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { clsx } from 'clsx';
import {
  Card,
  Button,
  Input,
  Badge,
  LoadingSpinner,
  EmptyState,
  Modal,
} from '../../../components/common';
import {
  useTenantList,
  useDeleteTenant,
} from '../../../hooks/useSuperAdmin';
import type {
  TenantListItem,
  TenantPlan,
  TenantStatusType,
  ListTenantsParams,
} from '../../../types/superadmin';
import { TenantCreateModal } from '../components/TenantCreateModal';
import { TenantEditModal } from '../components/TenantEditModal';
import { TenantStatusModal } from '../components/TenantStatusModal';
import { TenantPlanModal } from '../components/TenantPlanModal';

export const TenantsTab: React.FC = () => {
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatusType | ''>('');
  const [planFilter, setPlanFilter] = useState<TenantPlan | ''>('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<ListTenantsParams['sortBy']>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantListItem | null>(null);
  const [statusTenant, setStatusTenant] = useState<TenantListItem | null>(null);
  const [planTenant, setPlanTenant] = useState<TenantListItem | null>(null);
  const [deletingTenant, setDeletingTenant] = useState<TenantListItem | null>(null);

  // Query
  const {
    data,
    isLoading,
    refetch,
  } = useTenantList({
    page,
    limit: 20,
    search: searchQuery || undefined,
    status: statusFilter || undefined,
    plan: planFilter || undefined,
    sortBy,
    sortOrder,
  });

  const deletetenantMutation = useDeleteTenant();

  // Handlers
  const handleDelete = async () => {
    if (!deletingTenant) return;
    try {
      await deletetenantMutation.mutateAsync(deletingTenant.id);
      setDeletingTenant(null);
      refetch();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleSort = (field: ListTenantsParams['sortBy']) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Badge variants
  const getPlanBadgeVariant = (plan: string) => {
    switch (plan) {
      case 'enterprise':
        return 'primary';
      case 'professional':
        return 'secondary';
      case 'basic':
        return 'success';
      case 'trial':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'danger';
      case 'trial':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'suspended':
        return <XCircle className="w-4 h-4" />;
      case 'trial':
        return <Clock className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Tenants</h2>
          <p className="text-sm text-stone-500">
            {data?.total || 0} organizations on the platform
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', isLoading && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Tenant
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                type="text"
                placeholder="Search tenants..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as TenantStatusType | '');
              setPage(1);
            }}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="suspended">Suspended</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => {
              setPlanFilter(e.target.value as TenantPlan | '');
              setPage(1);
            }}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Plans</option>
            <option value="trial">Trial</option>
            <option value="basic">Basic</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
      </Card>

      {/* Tenants Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !data?.tenants.length ? (
        <EmptyState
          icon={Building2}
          title="No tenants found"
          description={
            searchQuery || statusFilter || planFilter
              ? 'No tenants match your search criteria'
              : 'Create your first tenant to get started'
          }
          action={{
            label: 'Add Tenant',
            onClick: () => setIsCreateModalOpen(true),
          }}
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-stone-200">
                <thead className="bg-stone-100">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-sand"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Organization
                        {sortBy === 'name' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-sand"
                      onClick={() => handleSort('plan')}
                    >
                      <div className="flex items-center gap-1">
                        Plan
                        {sortBy === 'plan' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-sand"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortBy === 'status' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-sand"
                      onClick={() => handleSort('userCount')}
                    >
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        Users
                        {sortBy === 'userCount' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-sand"
                      onClick={() => handleSort('okrCount')}
                    >
                      <div className="flex items-center gap-1">
                        <Target className="w-4 h-4" />
                        OKRs
                        {sortBy === 'okrCount' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-sand"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        {sortBy === 'createdAt' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-stone-200">
                  {data.tenants.map((tenant) => (
                    <tr key={tenant.id} className="hover:bg-sand">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 flex-shrink-0 bg-indigo-100 rounded-lg flex items-center justify-center">
                            {tenant.logo ? (
                              <img
                                src={tenant.logo}
                                alt={tenant.name}
                                className="w-8 h-8 rounded"
                              />
                            ) : (
                              <Building2 className="w-5 h-5 text-indigo-600" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-stone-900">
                              {tenant.name}
                            </div>
                            {tenant.trialEndsAt && tenant.status === 'trial' && (
                              <div className="text-xs text-orange-600">
                                Trial ends {format(new Date(tenant.trialEndsAt), 'MMM d, yyyy')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getPlanBadgeVariant(tenant.plan)}>
                          <CreditCard className="w-3 h-3 mr-1" />
                          {tenant.plan}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getStatusBadgeVariant(tenant.status)}>
                          {getStatusIcon(tenant.status)}
                          <span className="ml-1">{tenant.status}</span>
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-900">
                        {tenant.userCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-900">
                        {tenant.okrCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                        {tenant.lastActivityAt
                          ? formatDistanceToNow(new Date(tenant.lastActivityAt), {
                              addSuffix: true,
                            })
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                        {format(new Date(tenant.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setPlanTenant(tenant)}
                            className="p-2 text-stone-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Change plan"
                          >
                            <CreditCard className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setStatusTenant(tenant)}
                            className="p-2 text-stone-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Change status"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingTenant(tenant)}
                            className="p-2 text-stone-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Edit tenant"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeletingTenant(tenant)}
                            className="p-2 text-stone-400 hover:text-danger-600 hover:bg-danger-50 rounded"
                            title="Delete tenant"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-500">
                Showing {(page - 1) * 20 + 1} to{' '}
                {Math.min(page * 20, data.total)} of {data.total} tenants
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page === data.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Tenant Modal */}
      <TenantCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          refetch();
        }}
      />

      {/* Edit Tenant Modal */}
      {editingTenant && (
        <TenantEditModal
          tenant={editingTenant}
          isOpen={!!editingTenant}
          onClose={() => setEditingTenant(null)}
          onSuccess={() => {
            setEditingTenant(null);
            refetch();
          }}
        />
      )}

      {/* Status Change Modal */}
      {statusTenant && (
        <TenantStatusModal
          tenant={statusTenant}
          isOpen={!!statusTenant}
          onClose={() => setStatusTenant(null)}
          onSuccess={() => {
            setStatusTenant(null);
            refetch();
          }}
        />
      )}

      {/* Plan Change Modal */}
      {planTenant && (
        <TenantPlanModal
          tenant={planTenant}
          isOpen={!!planTenant}
          onClose={() => setPlanTenant(null)}
          onSuccess={() => {
            setPlanTenant(null);
            refetch();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingTenant}
        onClose={() => setDeletingTenant(null)}
        title="Delete Tenant"
      >
        <div className="space-y-4">
          <p className="text-stone-600">
            Are you sure you want to delete{' '}
            <strong className="text-stone-900">{deletingTenant?.name}</strong>?
          </p>
          <p className="text-sm text-danger-600">
            This will soft-delete the tenant and all their users will lose access.
            This action cannot be easily undone.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setDeletingTenant(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deletetenantMutation.isPending}
            >
              {deletetenantMutation.isPending ? 'Deleting...' : 'Delete Tenant'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
